import * as React from "react";
import {
    CheckmarkCircleFilled,
    DismissCircleFilled,
    DismissRegular,
    ChevronDownRegular,
    ChevronRightRegular,
    PlayCircleRegular,
    ClockRegular,
    BranchRegular,
    NumberSymbolSquareRegular,
} from "@fluentui/react-icons";
import { useHealReportViewStyles } from "../styles/healReportView.styles";
import type { ProjectVideoFile, SessionResponse } from "../api/models/ApiModels";
import { TestResultsModel, type TestCase } from "../models/TestResultModels";
import { findRecordingForVideoPath, mapRecordingsToVideoFiles } from "../utils/recordings";
import { useVideoPlayer } from "../utils/useVideoPlayer";
import { fetchJsonWithRetry } from "../utils/fetchJson";

/* ── Scout report JSON schema ── */

interface ConsideredTest {
    name: string;
    reason: string;
}

interface ScoutTest {
    /** Spec file path, exactly as Playwright reports it (relative to rootDir). */
    file: string;
    testName: string;
    status: "passed" | "failed" | "skipped";
    description?: string | null;
    rationale?: string | null;
    healAction?: string | null;
    notes?: string | null;
}

interface ScoutReport {
    metadata: {
        timestamp: string;
        branch: string;
        commitSha: string;
        prNumber?: number | null;
        gitRemoteUrl?: string | null;
        commitUrl?: string | null;
        branchUrl?: string | null;
        prUrl?: string | null;
        userInstruction?: string | null;
        /** testDir relative to the repo root, set by the CLI; joined with `file`
         *  to build repo-root links. */
        repoTestRoot?: string | null;
    };
    plan: {
        candidatesProposed: number;
        candidatesSelected: number;
        candidatesRejected: number;
        consideredTests?: ConsideredTest[];
    };
    tests: ScoutTest[];
    summary: {
        totalGenerated: number;
        passed: number;
        failed: number;
        healed: number;
        removed: number;
        text: string;
    };
}

interface ScoutReportViewProps {
    session: SessionResponse;
    onClose: () => void;
    projectId?: string;
}

/* ── Helpers ── */

function testTitle(name: string): string {
    const parts = name.split(" > ");
    return parts[parts.length - 1] ?? name;
}

function testGroup(name: string): string {
    const parts = name.split(" > ");
    return parts.length > 1 ? parts.slice(0, -1).join(" > ") : "";
}

function displayFileName(filePath: string): string {
    return filePath.split("/").pop() ?? filePath;
}

function normalizeAdoUrl(url: string | null | undefined): string | null | undefined {
    if (!url) return url;
    return url.replace(/\/(DefaultCollection)\//i, "/");
}

/** Join the report's `repoTestRoot` prefix with a test's `file` (relative to
 *  rootDir) to form the repo-root path used in source links. Null when no prefix
 *  is known (file then isn't safely linkable). */
function repoFilePath(repoTestRoot: string | null | undefined, file: string | null | undefined): string | null {
    if (!file || repoTestRoot == null) return null;
    const f = file.replace(/\\/g, "/").replace(/^\/+/, "");
    const prefix = repoTestRoot.replace(/\\/g, "/").replace(/^\/+|\/+$/g, "");
    return prefix ? `${prefix}/${f}` : f;
}

function buildFileUrl(gitRemoteUrl: string | null | undefined, commitSha: string | null | undefined, repoPath: string | null | undefined): string | null {
    if (!gitRemoteUrl || !commitSha || !repoPath) return null;
    const remote = normalizeAdoUrl(gitRemoteUrl.replace(/\/$/, ""))!;
    const path = `/${repoPath.replace(/^\/+/, "")}`;
    if (remote.includes("github.com")) {
        return `${remote}/blob/${commitSha}${path}`;
    }
    if (remote.includes("dev.azure.com") || remote.includes("visualstudio.com")) {
        return `${remote}?path=${path}&version=GC${commitSha}`;
    }
    return null;
}

/* ── Recording helpers ──
 *
 * Scout uses the EXACT SAME PIPELINE as the run view (ProjectDashboard):
 *   1. Parse test-results.json via TestResultsModel.fromRaw → TestCase[]
 *   2. Convert session.recordings → ProjectVideoFile[] via mapRecordingsToVideoFiles
 *   3. Match TestCase.primaryVideoPath → recording via findRecordingForVideoPath
 *
 * The only scout-specific bit is the join from a scout-report `tests[]`
 * entry (LLM-written, has description/rationale) → its matching TestCase
 * (Playwright-written, has the attachment path). We join by file basename
 * + leaf title. Everything past that point is identical to run. */

/* ── Component ── */

const ScoutReportView: React.FC<ScoutReportViewProps> = ({ session, onClose, projectId }) => {
    const s = useHealReportViewStyles();
    const [report, setReport] = React.useState<ScoutReport | null>(null);
    const [testResults, setTestResults] = React.useState<TestResultsModel | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const { playRecording, modal: videoModal, isPlaying, close: closeVideo } = useVideoPlayer(projectId);

    // Same shape `ProjectDashboard` (run view) uses — single source of truth.
    const videoFiles: ProjectVideoFile[] = React.useMemo(
        () => mapRecordingsToVideoFiles(session.recordings),
        [session]
    );

    const reportSasUrl = React.useMemo(
        () => session.files?.find((f) => f.fileType.trim().toLowerCase() === "scout-report" && !!f.sasUri)?.sasUri ?? null,
        [session]
    );

    // Playwright's own test-results.json — uploaded by every scout/heal/run
    // session. Same file the run view consumes; we parse it with the same
    // TestResultsModel.fromRaw so the test → video attachment mapping is
    // identical to what TestResultsTable renders for run.
    const pwReportSasUrl = React.useMemo(
        () => session.files?.find((f) => f.fileType.trim().toLowerCase() === "test-results" && !!f.sasUri)?.sasUri ?? null,
        [session]
    );

    React.useEffect(() => {
        if (!reportSasUrl) { setLoading(false); setError("No scout report data available for this session."); return; }
        const ctrl = new AbortController();
        const fetchUrl = import.meta.env.DEV ? `/blob-proxy?url=${encodeURIComponent(reportSasUrl)}` : reportSasUrl;
        fetchJsonWithRetry<ScoutReport>(fetchUrl, ctrl.signal)
            .then((data) => { setReport(data); setLoading(false); })
            .catch((err) => {
                if (ctrl.signal.aborted) return;
                console.error("[ScoutReportView] scout-report.json fetch failed", err);
                setError(`Failed to load report: ${err.message}`);
                setLoading(false);
            });
        return () => ctrl.abort();
    }, [reportSasUrl]);

    // Best-effort: failure to load test-results.json just means no videos.
    React.useEffect(() => {
        if (!pwReportSasUrl) { setTestResults(null); return; }
        const ctrl = new AbortController();
        const fetchUrl = import.meta.env.DEV ? `/blob-proxy?url=${encodeURIComponent(pwReportSasUrl)}` : pwReportSasUrl;
        fetchJsonWithRetry<Parameters<typeof TestResultsModel.fromRaw>[0]>(fetchUrl, ctrl.signal)
            .then((data) => { if (data) setTestResults(TestResultsModel.fromRaw(data)); })
            .catch((err) => {
                if (ctrl.signal.aborted) return;
                console.error("[ScoutReportView] test-results.json fetch failed", err);
                setTestResults(null);
            });
        return () => ctrl.abort();
    }, [pwReportSasUrl]);

    /** Join a scout-report test entry to the matching Playwright TestCase by file
     *  basename + leaf title (case-insensitive). */
    const findTestCaseForScout = React.useCallback((test: ScoutTest): TestCase | undefined => {
        if (!testResults) return undefined;
        const all = testResults.allTestCases;
        const leafTitle = testTitle(test.testName).toLowerCase();
        const candidateBase = (test.file.replace(/\\/g, "/").split("/").pop() ?? "").toLowerCase();
        return all.find((tc) => {
            const tcBase = (tc.file.replace(/\\/g, "/").split("/").pop() ?? "").toLowerCase();
            if (tcBase !== candidateBase) return false;
            const tcTitle = tc.title.toLowerCase();
            return tcTitle === leafTitle || tcTitle === test.testName.toLowerCase();
        });
    }, [testResults]);

    /** Identical pipeline to ProjectDashboard.handleWatchVideo (run view):
     *  TestCase.primaryVideoPath → findRecordingForVideoPath → ProjectVideoFile. */
    const resolveScoutRecording = React.useCallback((test: ScoutTest): ProjectVideoFile | undefined => {
        const tc = findTestCaseForScout(test);
        return findRecordingForVideoPath(tc?.primaryVideoPath, videoFiles);
    }, [findTestCaseForScout, videoFiles]);

    React.useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return;
            if (isPlaying) closeVideo();
            else onClose();
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onClose, isPlaying, closeVideo]);

    const meta = report?.metadata;
    const commitUrl = normalizeAdoUrl(meta?.commitUrl);
    const branchUrl = normalizeAdoUrl(meta?.branchUrl);
    const prUrl = normalizeAdoUrl(meta?.prUrl);
    const timestamp = meta?.timestamp ?? session.completedDateTime ?? session.createdDateTime;
    const formattedTime = timestamp
        ? new Date(timestamp).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
        : "";

    const passedTests = report?.tests.filter((t) => t.status === "passed") ?? [];
    const failedTests = report?.tests.filter((t) => t.status === "failed") ?? [];

    const consideredTests: ConsideredTest[] = report?.plan.consideredTests ?? [];

    return (
        <div className={s.overlay}>
            {/* Header */}
            <div className={s.header}>
                <div className={s.headerLeft}>
                    <img src="/icons/app-icon.svg" alt="" className={s.headerLogo} />
                    <span className={s.headerTitleGroup}>
                        <span className={s.headerTitle}>Scout Report</span>
                        <span className={s.badgeScout}>SCOUT</span>
                    </span>
                    {formattedTime && (
                        <span className={s.headerMetaItem}>
                            <span className={s.headerMetaIcon}><ClockRegular fontSize={16} /></span>
                            <span className={s.headerMeta}>{formattedTime}</span>
                        </span>
                    )}
                    {meta?.branch && (
                        <span className={s.headerMetaItem}>
                            <span className={s.headerMetaIcon}><BranchRegular fontSize={16} /></span>
                            {branchUrl
                                ? <a href={branchUrl} target="_blank" rel="noopener noreferrer" className={s.headerMetaLink}>{meta.branch}</a>
                                : <span className={s.headerMeta}>{meta.branch}</span>}
                        </span>
                    )}
                    {meta?.commitSha && (
                        <span className={s.headerMetaItem}>
                            <span className={s.headerMetaIcon}><NumberSymbolSquareRegular fontSize={16} /></span>
                            {commitUrl
                                ? <a href={commitUrl} target="_blank" rel="noopener noreferrer" className={s.headerCommit}>{meta.commitSha.substring(0, 7)}</a>
                                : <span className={s.headerCommit}>{meta.commitSha.substring(0, 7)}</span>}
                        </span>
                    )}
                    {meta?.prNumber && (
                        prUrl
                            ? <a href={prUrl} target="_blank" rel="noopener noreferrer" className={s.headerPr}>PR #{meta.prNumber}</a>
                            : <span className={s.headerPr}>PR #{meta.prNumber}</span>
                    )}
                </div>
                <button className={s.closeBtn} onClick={onClose} aria-label="Close"><DismissRegular /></button>
            </div>

            {/* Body */}
            <div className={s.body}>
                <div className={s.reportPane}>
                    <div className={s.reportContent}>
                        {loading && <div className={s.loadingCenter}>Loading scout report…</div>}
                        {error && <div className={s.errorCenter}>{error}</div>}

                        {report && (
                            <>
                                {/* Summary banner */}
                                <div className={s.summaryBanner}>
                                    <div className={s.summaryStats}>
                                        <SummaryStat count={report.summary.totalGenerated} label="Tests Added" color="#3fb950" />
                                        <span className={s.summaryStatDivider} aria-hidden="true" />
                                        <SummaryStat count={report.summary.passed} label="Passing" color="#3fb950" />
                                        {report.summary.failed > 0 && (
                                            <>
                                                <span className={s.summaryStatDivider} aria-hidden="true" />
                                                <SummaryStat count={report.summary.failed} label="Failing" color="#f85149" />
                                            </>
                                        )}
                                        {report.summary.healed > 0 && (
                                            <>
                                                <span className={s.summaryStatDivider} aria-hidden="true" />
                                                <SummaryStat count={report.summary.healed} label="Healed" color="#58a6ff" />
                                            </>
                                        )}
                                        {report.plan.candidatesRejected > 0 && (
                                            <>
                                                <span className={s.summaryStatDivider} aria-hidden="true" />
                                                <SummaryStat count={report.plan.candidatesRejected} label="Not Added" color="#8b949e" />
                                            </>
                                        )}
                                    </div>
                                    <div className={s.summaryLabel}>Session summary</div>
                                    <div className={s.summaryText}>{report.summary.text}</div>
                                </div>

                                {/* New Tests Added */}
                                {report.tests.length > 0 && (
                                    <Section title={`Tests Added (${report.tests.length})`} color="#3fb950" defaultOpen>
                                        {[...passedTests, ...failedTests].map((t, i) => {
                                            const rec = resolveScoutRecording(t);
                                            const fileUrl = buildFileUrl(meta?.gitRemoteUrl, meta?.commitSha, repoFilePath(meta?.repoTestRoot, t.file));
                                            const isPassing = t.status === "passed";
                                            const statusColor = isPassing ? "#3fb950" : "#f85149";
                                            const displayFile = t.file;
                                            return (
                                                <div key={i} className={s.testCard} style={{ borderLeftColor: statusColor }}>
                                                    <div className={s.testCardTopRow}>
                                                        <div className={s.testCardLeft}>
                                                            <span className={s.testCardStatusIcon} style={{ color: statusColor }}>
                                                                {isPassing ? <CheckmarkCircleFilled /> : <DismissCircleFilled />}
                                                            </span>
                                                            <div className={s.testCardContent}>
                                                                <div className={s.testCardName}>{testTitle(t.testName)}</div>
                                                                <div className={s.testCardMeta}>
                                                                    {fileUrl
                                                                        ? <a href={fileUrl} target="_blank" rel="noopener noreferrer" className={s.testCardMetaPath} title={displayFile}>{testGroup(t.testName) || displayFileName(displayFile)}</a>
                                                                        : <span className={s.testCardMetaPath} title={displayFile}>{testGroup(t.testName) || displayFileName(displayFile)}</span>}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className={s.testCardRight}>
                                                            {rec?.url && (
                                                                <button
                                                                    onClick={() => playRecording(rec, testTitle(t.testName))}
                                                                    className={s.actionBtn}
                                                                >
                                                                    <PlayCircleRegular style={{ fontSize: 14 }} />
                                                                    Watch test
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {(t.description || t.rationale || (t.notes && !isPassing)) && (
                                                        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                                                            {t.description && (
                                                                <div className={`${s.infoRow} ${s.infoRowFixed}`}>
                                                                    <div style={{ fontSize: 13, color: "#c9d1d9", lineHeight: 1.5 }}>
                                                                        <span className={s.infoRowLabel}>Covers: </span>{t.description}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {t.rationale && (
                                                                <div className={`${s.infoRow} ${s.infoRowUnclassified}`}>
                                                                    <div style={{ fontSize: 13, color: "#c9d1d9", lineHeight: 1.5 }}>
                                                                        <span className={s.infoRowLabel}>Why added: </span>{t.rationale}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {t.notes && !isPassing && (
                                                                <div className={`${s.infoRow} ${s.infoRowAppbug}`}>
                                                                    <div style={{ fontSize: 13, color: "#c9d1d9", lineHeight: 1.5 }}>
                                                                        <span className={s.infoRowLabel} style={{ color: "#f85149" }}>Why failing: </span>{t.notes}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </Section>
                                )}

                                {/* Considered But Not Added */}
                                {consideredTests.length > 0 && (
                                    <Section title={`Not Added (${consideredTests.length})`} color="#8b949e" defaultOpen>
                                        {consideredTests.map((ct, i) => (
                                            <div key={i} className={s.testCard} style={{ borderLeftColor: "#484f58" }}>
                                                {ct.name && ct.name !== `Candidate ${i + 1}` && (
                                                    <div className={s.testCardName} style={{ marginBottom: 4 }}>{ct.name}</div>
                                                )}
                                                <div className={`${s.infoRow} ${s.infoRowUnclassified}`}>
                                                    <div style={{ fontSize: 13, color: "#8b949e", lineHeight: 1.5 }}>{ct.reason}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </Section>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {videoModal}
        </div>
    );
};

/* ── Sub-components ── */

const Section: React.FC<{ title: string; color: string; defaultOpen?: boolean; children: React.ReactNode }> = ({ title, color, defaultOpen = true, children }) => {
    const s = useHealReportViewStyles();
    const [open, setOpen] = React.useState(defaultOpen);
    return (
        <div style={{ marginBottom: 28 }}>
            <div className={s.sectionHeader} onClick={() => setOpen(!open)}>
                <span className={s.sectionChevron} style={{ color }}>
                    {open ? <ChevronDownRegular /> : <ChevronRightRegular />}
                </span>
                <span className={s.sectionTitle} style={{ color }}>{title}</span>
                <span className={s.sectionDivider} />
            </div>
            {open && children}
        </div>
    );
};

const SummaryStat: React.FC<{ count: number; label: string; color: string }> = ({ count, label, color }) => {
    const s = useHealReportViewStyles();
    return (
        <div className={s.summaryStatItem}>
            <div className={s.summaryStatHeader}>
                <span className={s.summaryStatDot} style={{ backgroundColor: color }} />
                <span className={s.summaryStatLabel}>{label}</span>
            </div>
            <div className={s.summaryStatCount} style={{ color }}>{count}</div>
        </div>
    );
};

export default ScoutReportView;
